import {
  createFileRoute,
  Link,
  Outlet,
  useParams,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/hooks/trpc";
import { cn } from "@/lib/utils";
import { Settings, Plus, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { QuickRigId } from "@brewbase/db/types";

export const Route = createFileRoute("/devices")({
  component: DevicesLayout,
});

interface SensorState {
  name: string;
  temp: number | null;
  min: number;
  max: number;
  last_read: string | null;
  error: string | null;
}

interface AirSensorReading {
  value: number | null;
  unit: string;
  last_changed: string | null;
  error: string | null;
}

interface GatewayDevice {
  id: string;
  device_type: string;
  name: string;
  address: string;
  online: boolean;
  state: {
    // kamoer_pump
    running?: boolean;
    speed?: number;
    tubing_diameter?: number;
    is_active?: boolean;
    last_keepalive?: string;
    consecutive_failures?: number;
    // rtd_node
    sensors?: Record<string, SensorState>;
    // air_sensor
    readings?: Record<string, AirSensorReading>;
    prefix?: string;
    last_tick?: string | null;
    error?: string | null;
  };
  presets?: Array<{ rpm: number; flow_rate: number }>;
}

export type { GatewayDevice, SensorState, AirSensorReading };

function DevicesLayout() {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const selectedId = (params as { deviceId?: string }).deviceId
    ? decodeURIComponent((params as { deviceId?: string }).deviceId!)
    : null;
  const selectedRigId = (params as { rigId?: string }).rigId
    ? Number((params as { rigId?: string }).rigId)
    : null;

  const rigsQuery = trpc.rigs.quickList.useQuery(undefined, {
    refetchInterval: 5_000,
  });
  const rigs = rigsQuery.data ?? [];

  const [newRigName, setNewRigName] = useState<string | null>(null);
  const createRig = trpc.rigs.quickCreate.useMutation({
    onSuccess: (rig) => {
      setNewRigName(null);
      utils.rigs.quickList.invalidate();
      navigate({
        to: "/devices/rigs/$rigId",
        params: { rigId: String(rig.id) },
      });
    },
    onError: (e) => toast.error(e.message),
  });

  const healthQuery = trpc.devices.health.useQuery(undefined, {
    refetchInterval: 10_000,
  });

  const devicesQuery = trpc.devices.list.useQuery(undefined, {
    refetchInterval: 3_000,
  });

  const registryQuery = trpc.deviceRegistry.list.useQuery(undefined, {
    refetchInterval: 3_000,
  });

  // Archived devices load only once asked for; the default list query stays
  // the shared cache other pages read.
  const [showArchived, setShowArchived] = useState(false);
  const archivedQuery = trpc.deviceRegistry.list.useQuery(
    { includeArchived: true },
    { enabled: showArchived },
  );
  const archivedDevices = (archivedQuery.data ?? []).filter(
    (d) => d.archived_at !== null,
  );

  const gatewayStatus = healthQuery.data?.status ?? "loading";
  const devices: GatewayDevice[] =
    (devicesQuery.data as { devices?: GatewayDevice[] })?.devices ?? [];
  const registryDevices = registryQuery.data ?? [];

  // Group devices by type
  const grouped = new Map<string, GatewayDevice[]>();
  for (const d of devices) {
    const list = grouped.get(d.device_type) ?? [];
    list.push(d);
    grouped.set(d.device_type, list);
  }

  const typeLabels: Record<string, string> = {
    kamoer_pump: "Pumps",
    rtd_node: "Temperature",
    air_sensor: "Air Quality",
  };

  return (
    <div className="flex h-full">
      {/* Left panel — device list */}
      <div className="flex w-[300px] shrink-0 flex-col border-r border-olive-6">
        {/* Gateway status + actions */}
        <div className="flex items-center justify-between border-b border-olive-6 px-3 py-2">
          <GatewayStatusDot status={gatewayStatus} />
          <Link
            to="/settings/devices"
            className="text-olive-11 hover:text-olive-12"
            aria-label="Device settings"
          >
            <Settings className="size-4" />
          </Link>
        </div>

        {/* Device list */}
        <div className="flex-1 overflow-auto">
          {/* Quick rigs — ad-hoc multi-device sessions */}
          <div className="border-b border-olive-6 pb-1">
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-xs text-olive-11">Quick rigs</span>
              <button
                onClick={() => setNewRigName("")}
                className="text-olive-11 hover:text-olive-12"
                aria-label="New rig"
              >
                <Plus className="size-3.5" />
              </button>
            </div>
            {newRigName !== null && (
              <div className="flex items-center gap-1 px-3 py-1">
                <Input
                  autoFocus
                  value={newRigName}
                  placeholder="Rig name"
                  onChange={(e) => setNewRigName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newRigName.trim())
                      createRig.mutate({ name: newRigName.trim() });
                    if (e.key === "Escape") setNewRigName(null);
                  }}
                  className="h-6 text-sm"
                />
                <button
                  onClick={() =>
                    newRigName.trim() &&
                    createRig.mutate({ name: newRigName.trim() })
                  }
                  className="text-green-11 hover:text-green-12"
                  aria-label="Create rig"
                >
                  <Check className="size-3.5" />
                </button>
                <button
                  onClick={() => setNewRigName(null)}
                  className="text-olive-11 hover:text-olive-12"
                  aria-label="Cancel"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            )}
            {rigs.map((rig) => (
              <Link
                key={rig.id}
                to="/devices/rigs/$rigId"
                params={{ rigId: String(rig.id) }}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-olive-3",
                  selectedRigId === rig.id && "bg-olive-4",
                )}
              >
                <div className="size-2 shrink-0 rounded-full bg-green-9" />
                <span className="min-w-0 flex-1 truncate">{rig.name}</span>
              </Link>
            ))}
          </div>

          {devices.length === 0 && registryDevices.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-olive-11">
              {gatewayStatus === "ok"
                ? "No devices. Run discovery or add manually."
                : "Gateway not connected."}
            </div>
          ) : (
            <>
              {Array.from(grouped.entries()).map(([type, typeDevices]) => (
              <div key={type}>
                <div className="px-3 py-1.5 text-xs text-olive-11">
                  {typeLabels[type] ?? type}
                </div>
                {typeDevices.map((device) => {
                  const isRunning = device.state.running === true;
                  const isSelected = device.id === selectedId;
                  return (
                    <Link
                      key={device.id}
                      to="/devices/$deviceId"
                      params={{ deviceId: encodeURIComponent(device.id) }}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-olive-3",
                        isSelected && "bg-olive-4",
                      )}
                    >
                      <div
                        className={cn(
                          "size-2 shrink-0 rounded-full",
                          isRunning && "bg-green-9 animate-pulse",
                          !isRunning && device.online && "bg-olive-9",
                          !device.online && "bg-red-9",
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {device.name}
                      </span>
                      <DeviceMetric device={device} />
                    </Link>
                  );
                })}
              </div>
            ))}
              {registryDevices.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs text-olive-11">
                    BrewLink
                  </div>
                  {registryDevices.map((device) => {
                    const isSelected = selectedId === `bb:${device.id}`;
                    return (
                      <Link
                        key={device.id}
                        to="/devices/$deviceId"
                        params={{
                          deviceId: encodeURIComponent(`bb:${device.id}`),
                        }}
                        className={cn(
                          "flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-olive-3",
                          isSelected && "bg-olive-4",
                        )}
                      >
                        <div
                          className={cn(
                            "size-2 shrink-0 rounded-full",
                            device.status === "ONLINE"
                              ? "bg-green-9"
                              : "bg-red-9",
                          )}
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {device.name ?? device.hardware_id}
                        </span>
                        <span className="font-mono text-xs text-olive-11">
                          {device.status === "ONLINE" ? "online" : "offline"}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <button
            onClick={() => setShowArchived((v) => !v)}
            className="w-full px-3 py-1.5 text-left text-xs text-olive-11 hover:text-olive-12"
          >
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
          {showArchived &&
            archivedDevices.map((device) => (
              <Link
                key={device.id}
                to="/devices/$deviceId"
                params={{ deviceId: encodeURIComponent(`bb:${device.id}`) }}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 text-sm text-olive-11 hover:bg-olive-3",
                  selectedId === `bb:${device.id}` && "bg-olive-4",
                )}
              >
                <div className="size-2 shrink-0 rounded-full bg-olive-7" />
                <span className="min-w-0 flex-1 truncate">
                  {device.name ?? device.hardware_id}
                </span>
                <span className="font-mono text-xs">archived</span>
              </Link>
            ))}
          {showArchived &&
            archivedQuery.isSuccess &&
            archivedDevices.length === 0 && (
              <div className="px-3 py-1.5 text-xs text-olive-11">
                No archived devices
              </div>
            )}
        </div>

      </div>

      {/* Right panel — detail */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}

function DeviceMetric({ device }: { device: GatewayDevice }) {
  const isRunning = device.state.running === true;

  if (!device.online) {
    return <span className="text-xs text-red-11">Offline</span>;
  }

  if (device.device_type === "kamoer_pump") {
    if (isRunning && device.state.speed != null) {
      return (
        <span className="font-mono text-xs text-green-11">
          {String(device.state.speed)} rpm
        </span>
      );
    }
    if (isRunning) {
      return <span className="text-xs text-green-11">Running</span>;
    }
    return <span className="text-xs text-olive-11">Stopped</span>;
  }

  if (device.device_type === "rtd_node") {
    const sensors = device.state.sensors;
    if (!sensors) return null;
    const values = Object.values(sensors)
      .map((s) => (s.temp != null ? s.temp.toFixed(1) : "—"))
      .join(" / ");
    return (
      <span className="font-mono text-xs text-olive-11">{values} °C</span>
    );
  }

  if (device.device_type === "air_sensor") {
    const co2 = device.state.readings?.co2;
    if (!co2 || co2.value == null) return null;
    return (
      <span className="font-mono text-xs text-olive-11">
        {co2.value.toFixed(0)} ppm
      </span>
    );
  }

  return null;
}

function GatewayStatusDot({ status }: { status: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-olive-11">
      <div
        className={cn(
          "size-2 rounded-full",
          status === "ok" && "bg-green-9",
          status === "unreachable" && "bg-red-9",
          status === "not_configured" && "bg-olive-8",
          status === "loading" && "bg-olive-8 animate-pulse",
        )}
      />
      {status === "ok"
        ? "Gateway"
        : status === "unreachable"
          ? "Offline"
          : status === "not_configured"
            ? "Not configured"
            : "Connecting..."}
    </div>
  );
}

